import React, { useEffect } from 'react';
import { useRouteError } from 'react-router';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { captureException } from '@/lib/sentry/sentry-utils';
import { reloading } from '@/lib/hard-reload';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component for React 19
 *
 * React 19 improvements:
 * - Reduces duplicate error logs caught in componentDidCatch
 * - Bails out after first error when multiple siblings throw
 * - Only throws one error instead of duplicates
 *
 * @see https://andrei-calazans.com/posts/react-19-error-boundary-changed/
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('Error caught by ErrorBoundary:', error);
      console.error('Error info:', errorInfo);
    }

    // Capture error to Sentry with full context
    captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorBoundary: 'true',
        errorBoundaryName: this.constructor.name,
      },
      extra: {
        errorInfo,
        errorMessage: error.message,
        errorStack: error.stack,
      },
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  componentWillUnmount(): void {
    // Cleanup: Reset error state when component unmounts
    // This ensures no stale error state persists if the boundary is remounted
    // Future: Add cleanup for any subscriptions or timers if they are added
    this.setState({
      hasError: false,
      error: null,
    });
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
      }

      // Default fallback UI
      return <DefaultErrorFallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

/**
 * Default error fallback component with a nice UI
 */
function DefaultErrorFallback({ error, resetError }: FallbackProps) {
  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className='flex min-h-screen items-center justify-center p-4 bg-muted/30'>
      <Card className='w-full max-w-2xl'>
        <CardHeader>
          <div className='flex items-center gap-3'>
            <div className='flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10'>
              <AlertTriangle className='h-6 w-6 text-destructive' />
            </div>
            <div>
              <CardTitle className='text-2xl'>Something went wrong</CardTitle>
              <CardDescription>An unexpected error occurred in the application</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          <Alert variant='destructive'>
            <AlertTriangle className='h-4 w-4' />
            <AlertTitle>Error Details</AlertTitle>
            <AlertDescription className='mt-2 font-mono text-sm break-all'>{error.message}</AlertDescription>
          </Alert>

          {import.meta.env.DEV && error.stack && (
            <details className='rounded-lg border p-4'>
              <summary className='cursor-pointer font-medium text-sm'>Stack Trace (Development Only)</summary>
              <pre className='mt-3 overflow-auto text-xs text-muted-foreground whitespace-pre-wrap break-all'>{error.stack}</pre>
            </details>
          )}

          <div className='flex flex-col sm:flex-row gap-3 pt-2'>
            <Button onClick={resetError} className='flex-1'>
              <RefreshCcw className='mr-2 h-4 w-4' />
              Try Again
            </Button>
            <Button onClick={handleGoHome} variant='outline' className='flex-1'>
              <Home className='mr-2 h-4 w-4' />
              Go to Dashboard
            </Button>
          </div>

          <p className='text-xs text-muted-foreground text-center'>If this problem persists, please contact support.</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The router's `errorElement`. React Router catches whatever a route throws while loading or
 * rendering and shows its own bare "Unexpected Application Error!" page, so <ErrorBoundary>
 * above the router never sees it and neither does Sentry. This puts those errors on the same
 * screen and reports them.
 */
export function RouteErrorFallback() {
  const thrown = useRouteError();
  const error = thrown instanceof Error ? thrown : new Error(String((thrown as { statusText?: string })?.statusText ?? thrown));

  useEffect(() => {
    if (reloading) return;
    captureException(error, { tags: { errorBoundary: 'route' } });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per thrown value; `error` is rebuilt each render
  }, [thrown]);

  // A page from a build that is gone: main.tsx is already reloading onto the new one.
  if (reloading) return null;

  return <DefaultErrorFallback error={error} resetError={() => window.location.reload()} />;
}

export default ErrorBoundary;
